import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppShell from '@/components/layout/AppShell';
import supportService from '@/services/supportService';
import { TicketCategory, TicketPriority } from '@/types';
import toast from 'react-hot-toast';

interface NewTicketForm {
  subject: string;
  category: TicketCategory;
  priority: TicketPriority;
  description: string;
  attachment?: FileList;
}

const NewTicketPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<NewTicketForm>();

  const mutation = useMutation({
    mutationFn: (data: FormData) => supportService.createTicket(data),
    onSuccess: () => {
      toast.success('Ticket créé avec succès');
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      navigate('/support');
    },
    onError: () => {
      toast.error('Erreur lors de la création du ticket');
    }
  });

  const onSubmit = (data: NewTicketForm) => {
    const formData = new FormData();
    formData.append('subject', data.subject);
    formData.append('category', data.category);
    formData.append('priority', data.priority);
    formData.append('description', data.description);
    
    if (selectedFile) {
        formData.append('attachment', selectedFile);
    }

    mutation.mutate(formData);
  };

  return (
    <AppShell title="Contacter le support" showBack backLabel="Support">
      <div className="flex flex-col gap-6 p-4 md:p-0 md:pt-4 md:max-w-2xl">
         <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
             {/* Category */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-[#111813] dark:text-white">Catégorie</label>
                <div className="relative">
                  <select
                    {...register('category', { required: 'Catégorie requise' })}
                    className="w-full appearance-none rounded-xl border border-gray-200 bg-white p-3 pl-10 text-sm outline-none focus:border-primary dark:border-white/10 dark:bg-surface-dark dark:text-white"
                  >
                    <option value="">Sélectionner...</option>
                    <option value={TicketCategory.CLAIM}>Réclamation</option>
                    <option value={TicketCategory.TECHNICAL}>Technique</option>
                    <option value={TicketCategory.BILLING}>Remboursement</option>
                    <option value={TicketCategory.ACCOUNT}>Compte</option>
                    <option value={TicketCategory.GENERAL}>Autre</option>
                  </select>
                  <MaterialIcon icon="category" className="absolute left-3 top-3 text-gray-400" />
                  <MaterialIcon icon="expand_more" className="absolute right-3 top-3 text-gray-400" />
                </div>
                {errors.category && <span className="text-xs text-red-500">{errors.category.message}</span>}
              </div>

               {/* Priority */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-[#111813] dark:text-white">Priorité</label>
                <div className="relative">
                  <select
                    {...register('priority', { required: 'Priorité requise' })}
                    className="w-full appearance-none rounded-xl border border-gray-200 bg-white p-3 pl-10 text-sm outline-none focus:border-primary dark:border-white/10 dark:bg-surface-dark dark:text-white"
                  >
                     <option value={TicketPriority.LOW}>Basse</option>
                     <option value={TicketPriority.MEDIUM}>Moyenne</option>
                     <option value={TicketPriority.HIGH}>Haute</option>
                     <option value={TicketPriority.URGENT}>Urgente</option>
                  </select>
                  <MaterialIcon icon="priority_high" className="absolute left-3 top-3 text-gray-400" />
                   <MaterialIcon icon="expand_more" className="absolute right-3 top-3 text-gray-400" />
                </div>
                 {errors.priority && <span className="text-xs text-red-500">{errors.priority.message}</span>}
              </div>

             {/* Subject */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-[#111813] dark:text-white">Objet</label>
                <input
                    type="text"
                    {...register('subject', { required: 'Objet requis' })}
                    placeholder="Résumer votre demande"
                    className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none focus:border-primary dark:border-white/10 dark:bg-surface-dark dark:text-white"
                />
                 {errors.subject && <span className="text-xs text-red-500">{errors.subject.message}</span>}
              </div>

              {/* Description */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-[#111813] dark:text-white">Description détaillée</label>
                <textarea
                    rows={5}
                    {...register('description', { required: 'Description requise' })}
                    placeholder="Décrivez votre problème en détail..."
                    className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none focus:border-primary dark:border-white/10 dark:bg-surface-dark dark:text-white resize-none"
                />
                 {errors.description && <span className="text-xs text-red-500">{errors.description.message}</span>}
              </div>

              {/* Attachment */}
              <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-[#111813] dark:text-white">Pièce jointe (optionnel)</label>
                  <div className="relative border-2 border-dashed border-gray-200 dark:border-white/10 rounded-xl p-4 text-center cursor-pointer hover:border-primary/50 transition-colors bg-white dark:bg-surface-dark">
                      <input
                          type="file"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          onChange={(e) => {
                             if(e.target.files?.[0]) setSelectedFile(e.target.files[0]);
                          }}
                          accept=".pdf,.jpg,.jpeg,.png"
                      />
                       <div className="flex flex-col items-center gap-2 pointer-events-none">
                           <MaterialIcon icon="attach_file" className="text-2xl text-gray-400" />
                           {selectedFile ? (
                               <p className="text-sm font-medium text-primary line-clamp-1">{selectedFile.name}</p>
                           ) : (
                               <p className="text-sm text-gray-500">Cliquez pour ajouter un fichier</p>
                           )}
                       </div>
                  </div>
              </div>

              <button
                type="submit"
                disabled={mutation.isPending}
                className="mt-4 flex w-full items-center justify-center rounded-xl bg-primary py-3 font-bold text-[#102216] transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {mutation.isPending ? 'Envoi...' : 'Envoyer la demande'}
              </button>
         </form>
      </div>
    </AppShell>
  );
};

export default NewTicketPage;
